import pLimit from "p-limit";
import { filesFromDataTransfer } from "src/utils/clipboard-files";
import type React from "react";

import {
  BLOCKED_FILE_EXTENSIONS,
  getFileExtension,
  isBlockedFileExtension,
  isImageMimeType,
  lookupExtensionForMimeType,
  lookupMimeTypeForExtension,
  VALID_IMAGE_MIME_TYPES,
} from "@hightouch/lib/util/mime-types";
import {
  MAX_CHAT_UPLOAD_SIZE_BYTES,
  MAX_INLINE_ATTACHMENT_SIZE_BYTES,
} from "@hightouch/lib/llm/upload-limits";
import { fileToBase64 } from "src/ui/file/file-uploader";
import type { FileInput, SessionFileInput } from "src/graphql";
import { captureException } from "src/lib/monitoring";
import { compressImageBlob } from "src/utils/compress-image";
import { isUploadNetworkError } from "src/utils/put-with-progress";

// Backend will reject payloads larger than this
export const MAX_PAYLOAD_SIZE = 50 * 1024 * 1024; // 50MB

/**
 * Attachments are base64-encoded into the GraphQL mutation body, and the
 * backend's request-body limit applies to that *encoded* body. Base64 inflates
 * by 4/3, and the rest of the mutation (prompt text, context, JSON envelope)
 * shares the same budget — so the raw bytes we can afford are well under
 * MAX_PAYLOAD_SIZE.
 *
 * Comparing raw bytes against the raw budget keeps the client-side check in the
 * same units the server enforces. Checking raw bytes against the encoded limit
 * lets a payload between the two sail past the client and get rejected by the
 * server as a plaintext 500, which the app cannot surface to the user.
 */
const BASE64_INFLATION = 4 / 3;
const MUTATION_ENVELOPE_ALLOWANCE = 2 * 1024 * 1024; // 2MB

export const maxRawAttachmentBytes = (
  maxPayloadSize: number = MAX_PAYLOAD_SIZE,
): number =>
  Math.max(
    0,
    Math.floor(
      (maxPayloadSize - MUTATION_ENVELOPE_ALLOWANCE) / BASE64_INFLATION,
    ),
  );

/**
 * Kept as its own constant even though it currently equals the general cap:
 * it produces a per-file error naming the video rather than a batch-total one,
 * and video is the attachment most likely to need a lower ceiling again. The
 * agent re-encodes every video through ffmpeg under a 10-minute transcode
 * timeout, so a long clip can fail late no matter how fast it uploaded.
 */
export const MAX_VIDEO_FILE_SIZE = MAX_CHAT_UPLOAD_SIZE_BYTES;
export const VIDEO_EXTENSIONS = [".mp4", ".mov"] as const;

export { BLOCKED_FILE_EXTENSIONS };

export function assertFilesHaveAllowedExtensions(
  files: Array<{ name: string }>,
  allowedExtensions: readonly string[],
): void {
  const normalizedExtensions = new Set(
    allowedExtensions.map((extension) => extension.toLowerCase()),
  );
  const unsupportedFiles = files.filter(
    (file) => !normalizedExtensions.has(getFileExtension(file.name)),
  );

  if (unsupportedFiles.length > 0) {
    throw new Error(
      `Unsupported file type: ${unsupportedFiles.map((file) => file.name).join(", ")}. Supported types: ${allowedExtensions.join(", ")}.`,
    );
  }
}

/**
 * Rejects files on the {@link BLOCKED_FILE_EXTENSIONS} denylist. Enforced in
 * both {@link processFilesToFileInput} and {@link processFilesToUploadedFiles}
 * so every attachment path — picker, paste, drop, and the agent-files upload —
 * shares one check regardless of what its `accept` attribute hinted.
 */
export function assertFilesAreNotBlockedTypes(
  files: Array<{ name: string }>,
): void {
  const blockedFiles = files.filter((file) =>
    isBlockedFileExtension(file.name),
  );

  if (blockedFiles.length > 0) {
    throw new Error(
      `Can't attach ${blockedFiles.map((file) => file.name).join(", ")}: executables, installers, and other binary program files aren't supported.`,
    );
  }
}

// Callers own the returned object URL and must revoke it when it is no longer
// needed.
export const base64ToBlobUrl = (
  base64Data: string,
  mimeType: string,
): string => {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return URL.createObjectURL(blob);
};

const hasVideoExtension = (fileName: string): boolean =>
  (VIDEO_EXTENSIONS as readonly string[]).includes(getFileExtension(fileName));

/**
 * MIME types that tell us nothing about the content — the browser reporting
 * that it did not recognize the file, rather than that it identified it as
 * binary. `.md`, `.markdown`, `.yaml`, `.tsv` and `.log` have no registered
 * type on most platforms, so `File.type` comes back empty for them; a few
 * platforms and S3 substitute a generic binary type instead.
 */
const UNINFORMATIVE_MIME_TYPES = new Set([
  "",
  "application/octet-stream",
  "binary/octet-stream",
]);

/**
 * Resolves the MIME type to record for an attachment, filling in from the
 * extension when the browser had nothing useful to offer.
 *
 * This matters beyond cosmetics: the MIME type is what we store on the S3
 * object, what the `<uploaded_files>` prompt section tells the agent the file
 * is, and what decides whether a preview renders as an image, a video poster or
 * a file card. An empty string propagates through all of that.
 */
export const resolveAttachmentMimeType = (
  mimeType: string,
  fileName?: string,
): string => {
  const fromExtension = fileName
    ? lookupMimeTypeForExtension(getFileExtension(fileName).slice(1))
    : undefined;

  let resolved = mimeType;
  if (fromExtension) {
    // A video MIME is load-bearing — the pill's `<video>` poster paints a blank
    // tile without one — and browsers routinely report nothing, or something
    // non-video, for .mov/.mp4. So for video the extension wins outright.
    if (fromExtension.startsWith("video/") && !mimeType.startsWith("video/")) {
      resolved = fromExtension;
    } else if (UNINFORMATIVE_MIME_TYPES.has(mimeType)) {
      resolved = fromExtension;
    }
  }

  // Attachments aren't restricted to a known-type allowlist, so a file can
  // reach here with an extension nothing maps and an empty browser-reported
  // type. Never record the empty string — it would become the S3 content type
  // and what the prompt tells the agent the file is.
  return resolved || "application/octet-stream";
};

/**
 * Images we can safely re-encode. GIF is excluded because re-encoding would
 * drop the animation. Non-image attachments (PDF, the text formats) and video
 * are never re-encoded.
 */
const COMPRESSIBLE_IMAGE_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
]);

/**
 * Below this an attachment is neither driving the payload budget nor wasting
 * meaningful image tokens, so skip the decode/re-encode round trip and send the
 * bytes untouched.
 */
const COMPRESS_ABOVE_BYTES = 100 * 1024;

const totalBytes = (files: File[]): number =>
  files.reduce((acc, file) => acc + file.size, 0);

const formatMb = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  // The cap is measured in GB now, and "1024.0MB" reads like a bug.
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)}GB` : `${mb.toFixed(1)}MB`;
};

// Compression picks its own encoding, so re-derive the extension rather than
// leaving a .png named file holding JPEG bytes. Unknown types keep their name.
const withExtensionFor = (fileName: string, contentType: string): string => {
  const extension = lookupExtensionForMimeType(contentType);
  return extension
    ? `${fileName.replace(/\.[^.]+$/, "")}.${extension}`
    : fileName;
};

/**
 * Downscale and re-encode an attachment image, the same way ad-studio
 * compresses style reference images. Keeps whichever of the original and the
 * compressed version is smaller, and returns the original untouched for
 * anything we can't or shouldn't re-encode.
 *
 * Compression is unconditional rather than triggered by an oversized batch:
 * gating on the batch total made the same image compress or not depending on
 * what else was attached, and left the compression path almost never exercised.
 * Every attachment above the threshold is a payload and image-token cost even
 * when the batch as a whole fits.
 *
 * Exported so the catalog and content-library pickers, which build FileInputs
 * without going through {@link processFilesToFileInput}, get the same handling.
 */
const imageProcessingLimit = pLimit(2);
export const compressAttachmentImage = (file: File): Promise<File> =>
  imageProcessingLimit(() => compressAttachmentImageBytes(file));

async function compressAttachmentImageBytes(file: File): Promise<File> {
  if (!COMPRESSIBLE_IMAGE_MIME_TYPES.has(file.type)) return file;
  if (file.size <= COMPRESS_ABOVE_BYTES) return file;

  try {
    const compressed = await compressImageBlob(file);
    if (compressed.size >= file.size) return file;
    return new File(
      [compressed],
      withExtensionFor(file.name, compressed.type),
      {
        type: compressed.type,
      },
    );
  } catch (error) {
    // A single undecodable image shouldn't fail the whole batch — send it
    // as-is and let the size check decide.
    captureException(error);
    return file;
  }
}

/**
 * Video is capped per file rather than only by the batch budget: the agent
 * runtime handles a long clip far worse than its byte count suggests.
 */
export function assertVideosAreNotOversized(
  files: File[],
  maxBytes: number = MAX_INLINE_DELIVERY_BYTES,
): void {
  // Whichever binds first: video's own ceiling, or what delivery can carry.
  const limit = Math.min(MAX_VIDEO_FILE_SIZE, maxBytes);
  for (const file of files) {
    if (hasVideoExtension(file.name) && file.size > limit) {
      throw new Error(
        `Video file "${file.name}" is ${formatMb(file.size)}. Video files must be under ${formatMb(limit)}.`,
      );
    }
  }
}

/**
 * An attachment already uploaded to storage, identified by key rather than
 * carried as bytes. Matches the backend's `SessionFileInput`.
 */
export type UploadedSessionFile = {
  s3Key: string;
  fileName: string;
  mimeType: string;
};

/** How much of a file has reached storage so far. */
export type UploadProgress = { loaded: number; total: number };

/** Where one attachment's bytes go, minted for it ahead of the transfer. */
export type UploadTarget = {
  uploadUrl: string;
  s3Key: string;
};

export type UploadAttachmentOptions = {
  /** Called as the bytes go up. Silent when the body cannot be sized. */
  onProgress?: (progress: UploadProgress) => void;
  /** Cancels the transfer; the upload rejects with an `AbortError`. */
  signal?: AbortSignal;
  /**
   * The presigned target for this file. Minted per selection rather than per
   * file, so the caller holding the selection supplies it.
   */
  target?: UploadTarget;
};

/** Uploads one file's bytes to storage and resolves to its storage key. */
export type UploadAttachment = (
  file: File,
  options?: UploadAttachmentOptions,
) => Promise<string>;

/**
 * Upload is only half the journey, so the cap tracks what *delivery* can carry.
 *
 * An attachment inlined into the agent payload is bounded by the budget that
 * ships in that payload — a bigger file would upload cleanly and then fail the
 * turn, which is worse than refusing it at pick time. An attachment the
 * runtime fetches by URL is bounded by the server's own upload limit instead:
 * sharing that constant keeps the client from rejecting a file the server would
 * take, or accepting one it would not.
 *
 * The conservative one is the default for the helpers below, so a caller that
 * forgets to pass a cap fails safe rather than promising more than delivery can
 * carry.
 */
export const MAX_INLINE_DELIVERY_BYTES = MAX_INLINE_ATTACHMENT_SIZE_BYTES;
export const MAX_URL_DELIVERY_BYTES = MAX_CHAT_UPLOAD_SIZE_BYTES;

/**
 * Compresses attachment images and uploads them straight to storage, returning
 * the keys to send on a chat mutation's `preUploadedFiles`.
 *
 * The bytes never enter the GraphQL body, which is what the encoded-payload
 * budget in {@link processFilesToFileInput} exists to bound. `uploadFile` is a
 * parameter so this stays testable without a network.
 *
 * @throws Error if a file is a blocked type, a video is over its per-file cap,
 * or the batch exceeds the size cap
 */
/**
 * Runs the gates and compression that must happen before anything is uploaded,
 * and returns the files that will actually be sent.
 *
 * Compression can rename a file (a re-encoded .png becomes .jpg), so callers
 * must carry these forward rather than the originals the user picked —
 * otherwise the uploaded object and the record of it disagree.
 *
 * @throws Error if a file is a blocked type, a video is over its per-file cap,
 * or the batch exceeds the size cap
 */
async function prepareAttachmentsForUpload(
  files: File[],
  maxBytes: number,
  compressImages: boolean,
): Promise<File[]> {
  // The same gates processFilesToFileInput applies. They have to be repeated
  // here rather than inherited: this path never calls that function, so
  // migrating a surface across would otherwise drop them with no visible change.
  assertFilesAreNotBlockedTypes(files);
  assertVideosAreNotOversized(files, maxBytes);

  const processed = compressImages
    ? await Promise.all(files.map(compressAttachmentImage))
    : files;

  const totalSize = totalBytes(processed);
  if (totalSize > maxBytes) {
    throw new Error(
      `Attachments total ${formatMb(totalSize)}${compressImages ? " after compression" : ""}, over the ${formatMb(maxBytes)} limit. Remove some files or shrink them and try again.`,
    );
  }

  return processed;
}

const uploadOne = async (
  file: File,
  uploadFile: UploadAttachment,
  options?: UploadAttachmentOptions,
): Promise<UploadedSessionFile> => ({
  s3Key: await uploadFile(file, options),
  fileName: file.name,
  mimeType: resolveAttachmentMimeType(file.type, file.name),
});

/**
 * Per file rather than summed: a batch mixing a 200MB PDF with a small image
 * spends nearly all its time on one of them, and one combined percentage says
 * nothing about which.
 */
export type BatchUploadOptions = {
  onFileProgress?: (file: File, progress: UploadProgress) => void;
  signal?: AbortSignal;
  /** Presigned targets, positional against the files passed in. */
  targets?: UploadTarget[];
  /**
   * Defaults to true. False uploads images as the original bytes, name and
   * type, for surfaces where the upload becomes a library asset.
   */
  compressImages?: boolean;
};

export async function processFilesToUploadedFiles(
  files: File[],
  uploadFile: UploadAttachment,
  maxBytes: number = MAX_INLINE_DELIVERY_BYTES,
  options?: BatchUploadOptions,
): Promise<UploadedSessionFile[]> {
  if (files.length === 0) {
    return [];
  }

  const prepared = await prepareAttachmentsForUpload(
    files,
    maxBytes,
    options?.compressImages ?? true,
  );
  return Promise.all(
    prepared.map((file, index) =>
      uploadOne(file, uploadFile, {
        // Keyed by the picked file: compression renames an image, so the
        // prepared one would open a second entry under a name the caller never
        // saw. `prepareAttachmentsForUpload` maps one-to-one.
        onProgress: (progress) =>
          options?.onFileProgress?.(files[index] ?? file, progress),
        signal: options?.signal,
        target: options?.targets?.[index],
      }),
    ),
  );
}

// Every inline attachment on a message shares one budget: they travel in one request body.
export const fitsInlineFallback = (
  bytes: number,
  inlineBytesInUse: number,
): boolean => inlineBytesInUse + bytes <= MAX_INLINE_ATTACHMENT_SIZE_BYTES;

/**
 * Claims inline budget for one file and reports whether it fit. One call does
 * both so two uploads failing together cannot each take the last of it.
 */
export type ReserveInlineBytes = (bytes: number) => boolean;

const batchInlineBudget = (): ReserveInlineBytes => {
  let used = 0;
  return (bytes) => {
    if (!fitsInlineFallback(bytes, used)) return false;
    used += bytes;
    return true;
  };
};

export type ComposerUploadOptions = BatchUploadOptions & {
  /** Defaults to a budget for this batch alone. */
  reserveInlineBytes?: ReserveInlineBytes;
};

/**
 * The upload got no response and the file does not fit the message's inline budget.
 * The copy names no cause because a blocked host and a dropped connection look the same here.
 */
export class InlineAttachmentLimitError extends Error {
  override readonly name = "InlineAttachmentLimitError";

  constructor(fileName: string, options?: ErrorOptions) {
    super(
      `Couldn't reach file storage to upload "${fileName}". When that happens, a message's attachments can total up to ${formatMb(MAX_INLINE_ATTACHMENT_SIZE_BYTES)}. Try again, or attach a smaller file.`,
      options,
    );
  }
}

/**
 * Some networks refuse the storage host but allow Hightouch, so a file whose
 * upload got no response goes inline in the chat mutation, which they can reach.
 */
async function uploadOrEncodeInline(
  file: File,
  uploadFile: UploadAttachment,
  options: UploadAttachmentOptions,
  reserveInlineBytes: ReserveInlineBytes,
): Promise<
  Pick<ComposerFile, "fileName" | "mimeType" | "s3Key" | "base64Data">
> {
  try {
    return await uploadOne(file, uploadFile, options);
  } catch (error) {
    if (options.signal?.aborted || !isUploadNetworkError(error)) throw error;
    if (!reserveInlineBytes(file.size)) {
      throw new InlineAttachmentLimitError(file.name, { cause: error });
    }
    return {
      fileName: file.name,
      mimeType: resolveAttachmentMimeType(file.type, file.name),
      base64Data: await fileToBase64(file),
    };
  }
}

/**
 * An attachment held by a composer, before it is split onto a chat mutation.
 *
 * An attachment either carries `s3Key` for a presigned upload, `base64Data` for
 * surfaces still encoding inline and for files whose upload the network
 * refused, or `catalogAssetId` for a server-resolved
 * catalog reference. `previewUrl` is a local object URL (revoked by the store)
 * or a remote catalog URL (no cleanup needed). `file` retains local bytes for
 * surfaces that bridge attachments into a different upload path.
 */
export type ComposerFile = Omit<FileInput, "base64Data"> & {
  id?: string;
  sizeBytes?: number;
  loadFile?: (signal: AbortSignal) => Promise<File>;
  base64Data?: string;
  s3Key?: string;
  previewUrl?: string;
  file?: File;
};

/**
 * Uploads attachments and pairs each with an object URL for the file the user
 * picked, so previews still render once the bytes stop travelling in the
 * mutation body.
 *
 * The URLs are the caller's to revoke — see the store in `file-context`, which
 * revokes an entry's URL when it leaves `files` and the rest on unmount.
 */
export async function processFilesToComposerFiles(
  files: File[],
  uploadFile: UploadAttachment,
  maxBytes: number = MAX_INLINE_DELIVERY_BYTES,
  options?: ComposerUploadOptions,
): Promise<ComposerFile[]> {
  if (files.length === 0) {
    return [];
  }

  // Pairs each upload with the file that was actually uploaded, not the one
  // the user picked. Indexing back into `files` would attach the uncompressed
  // original, so the preview, the retained bytes and the stored object could
  // all disagree — and the surfaces that still send bytes inline would undo
  // the compression this just did.
  const prepared = await prepareAttachmentsForUpload(
    files,
    maxBytes,
    options?.compressImages ?? true,
  );
  const reserveInlineBytes = options?.reserveInlineBytes ?? batchInlineBudget();

  return Promise.all(
    prepared.map(async (file, index) => ({
      ...(await uploadOrEncodeInline(
        file,
        uploadFile,
        {
          // Keyed by the picked file — see processFilesToUploadedFiles.
          onProgress: (progress) =>
            options?.onFileProgress?.(files[index] ?? file, progress),
          signal: options?.signal,
          target: options?.targets?.[index],
        },
        reserveInlineBytes,
      )),
      previewUrl: URL.createObjectURL(file),
      file,
    })),
  );
}

/**
 * The attachment fields to spread into a chat mutation's input. Each side is
 * omitted when empty so a message with no attachments sends neither field.
 */
export const chatFileFields = (
  composerFiles: ComposerFile[],
): {
  files: FileInput[] | undefined;
  preUploadedFiles: SessionFileInput[] | undefined;
} => {
  const { files, preUploadedFiles } = splitComposerFiles(composerFiles);
  return {
    files: files.length > 0 ? files : undefined,
    preUploadedFiles:
      preUploadedFiles.length > 0 ? preUploadedFiles : undefined,
  };
};

/**
 * Base64-encodes composer attachments for the mutations that still take inline
 * bytes only, such as the agent-file uploads.
 *
 * Entries that already carry `base64Data` pass through untouched. An entry with
 * neither the bytes nor the picked `File` is dropped: its content lives only in
 * storage, and this path has nowhere to put a key.
 */
export async function composerFilesToFileInputs(
  composerFiles: ComposerFile[],
): Promise<FileInput[]> {
  const encoded = await Promise.all(
    composerFiles.map(
      async ({
        previewUrl: _previewUrl,
        id: _id,
        sizeBytes: _sizeBytes,
        loadFile: _loadFile,
        s3Key: _s3Key,
        file,
        base64Data,
        ...rest
      }): Promise<FileInput | null> => {
        if (base64Data) return { ...rest, base64Data };
        if (file) return { ...rest, base64Data: await fileToBase64(file) };
        return null;
      },
    ),
  );

  return encoded.filter((file): file is FileInput => file !== null);
}

/**
 * Splits composer attachments into the two fields the chat mutations accept:
 * inline bytes on `files`, uploaded keys and catalog references on
 * `preUploadedFiles`. Both are sent together.
 *
 * Entries carrying neither are dropped rather than sent as an empty payload the
 * server would reject.
 */
export const splitComposerFiles = (
  composerFiles: ComposerFile[],
): { files: FileInput[]; preUploadedFiles: SessionFileInput[] } => {
  const files: FileInput[] = [];
  const preUploadedFiles: SessionFileInput[] = [];

  for (const {
    previewUrl: _previewUrl,
    id: _id,
    sizeBytes: _sizeBytes,
    loadFile: _loadFile,
    file: _file,
    s3Key,
    base64Data,
    ...rest
  } of composerFiles) {
    if (s3Key) {
      preUploadedFiles.push({ ...rest, s3Key });
    } else if (base64Data) {
      files.push({ ...rest, base64Data });
    } else if (rest.catalogAssetId) {
      preUploadedFiles.push(rest);
    }
  }

  return { files, preUploadedFiles };
};

/**
 * Compresses attachment images, validates the total against the request-body
 * budget, and converts File objects to FileInput format.
 * @param files - Array of File objects to process
 * @param maxSize - Maximum encoded request body in bytes (defaults to MAX_PAYLOAD_SIZE)
 * @returns Promise resolving to array of FileInput objects
 * @throws Error if the files still exceed the budget after compression
 */
export async function processFilesToFileInput(
  files: File[],
  maxSize: number = MAX_PAYLOAD_SIZE,
): Promise<FileInput[]> {
  if (files.length === 0) {
    return [];
  }

  assertFilesAreNotBlockedTypes(files);
  assertVideosAreNotOversized(files);

  const budget = maxRawAttachmentBytes(maxSize);
  const processed = await Promise.all(files.map(compressAttachmentImage));

  const totalSize = totalBytes(processed);
  if (totalSize > budget) {
    throw new Error(
      `Attachments total ${formatMb(totalSize)} after compression, over the ${formatMb(budget)} limit. Remove some files or shrink them and try again.`,
    );
  }

  // Convert files to FileInput format
  const filePromises = processed.map(async (file) => ({
    fileName: file.name,
    mimeType: resolveAttachmentMimeType(file.type, file.name),
    base64Data: await fileToBase64(file),
  }));

  return Promise.all(filePromises);
}

export const isImage = (mimeType: string): boolean => isImageMimeType(mimeType);

// Browsers frequently report empty or generic MIME types for .mov/.mp4, so we
// also treat known video extensions as video when a filename is available.
export const isVideo = (mimeType: string, fileName?: string): boolean =>
  mimeType.startsWith("video/") ||
  (fileName !== undefined && hasVideoExtension(fileName));

/**
 * Displayable src for a composing attachment's image preview: the object URL
 * for one uploaded straight to storage, the data URI for one still carrying
 * inline bytes. Null for anything that isn't an image, or that has no local
 * handle on its bytes.
 */
export const composerImageSrc = (file: ComposerFile): string | null => {
  if (!isImage(file.mimeType)) return null;
  if (file.previewUrl) return file.previewUrl;
  if (file.base64Data) return `data:${file.mimeType};base64,${file.base64Data}`;
  return null;
};

/** Uppercased extension without the dot ("MD"), or "" when there is none. */
export const fileExtensionLabel = (fileName: string): string =>
  getFileExtension(fileName).slice(1).toUpperCase();

/**
 * A `Map` rather than an object literal: MIME types arrive from the browser and
 * from the database, so an object lookup would return an inherited
 * `Object.prototype` member for a type named `toString` — a function that React
 * then throws on when it tries to render it as a label.
 */
const MIME_TYPE_LABELS = new Map<string, string>([
  ["application/pdf", "PDF"],
  ["text/plain", "Text"],
  ["text/markdown", "Markdown"],
  ["text/csv", "CSV"],
  ["text/tab-separated-values", "TSV"],
  ["application/json", "JSON"],
  ["text/html", "HTML"],
  ["text/xml", "XML"],
  ["application/xml", "XML"],
  ["application/yaml", "YAML"],
  ["text/yaml", "YAML"],
]);

/**
 * Human-readable type for an attachment preview ("Markdown", "CSV", "PDF").
 *
 * The `text/` prefix is the fallback rather than a longer lookup table because
 * platforms invent vendor subtypes freely — Chromium reports `.log` as
 * `text/x-log` — and every one of them is more usefully described as "Text"
 * than as "File".
 */
export const fileTypeDescription = (mimeType: string): string => {
  if (isImage(mimeType)) return "Image";
  if (isVideo(mimeType)) return "Video";
  if (mimeType.startsWith("audio/")) return "Audio";
  const label = MIME_TYPE_LABELS.get(mimeType);
  if (label) return label;
  return mimeType.startsWith("text/") ? "Text" : "File";
};

const PASTE_SUPPORTED_IMAGE_MIME_TYPES = new Set<string>(
  VALID_IMAGE_MIME_TYPES,
);

interface ClipboardFileExtraction {
  files: File[];
  rejectedMimeTypes: string[];
}

/**
 * Pulls files out of a paste event.
 *
 * Images are filtered to the canonical list of supported image MIME types, and
 * the rejects are returned separately so the caller can surface a clear
 * "Unsupported format" toast. Every other file type is taken as-is, matching
 * the drop path, which sets no `accept` filter and leaves the blocked-extension
 * denylist to the processing step both paths share.
 *
 * Any file on the clipboard wins over its text: copying a file in Finder or
 * Explorer puts both the file and a `text/plain` fallback of its name on the
 * clipboard, and pasting the name into the composer is never what was meant.
 */
export function extractFilesFromClipboardEvent(
  event: React.ClipboardEvent,
): ClipboardFileExtraction {
  const files: File[] = [];
  const rejectedMimeTypes: string[] = [];
  for (const file of filesFromDataTransfer(event.clipboardData)) {
    if (
      file.type.startsWith("image/") &&
      !PASTE_SUPPORTED_IMAGE_MIME_TYPES.has(file.type)
    ) {
      rejectedMimeTypes.push(file.type);
      continue;
    }
    files.push(file);
  }
  return { files, rejectedMimeTypes };
}
